/**
 * Scene Recording Controller - Sprint 15-16 Phase 4
 * Manages sequential multi-scene recording workflow
 */

import React, { useEffect, useCallback } from 'react';
import { useAtom } from 'jotai';
import { Button, Space, Progress, Typography, Card, Alert } from 'antd';
import { PlayCircle, Square, ChevronRight, ChevronLeft, Camera } from 'lucide-react';
import {
  currentSceneAtom,
  sceneListAtom,
  sceneRecordingStateAtom,
  nextSceneAtom,
  previousSceneAtom,
  activeProjectAtom,
  markSceneRecordedAtom,
  updateSceneInListAtom,
  showRecordingPreviewAtom,
  updateProjectDurationAtom,
} from '../../atoms/sceneAtoms';
import { Scene } from '../../../../types/scene';
import SceneService from '../../services/sceneService';
import logger from '../../utils/logger';
import { db } from '../../idb/chunk-db';
import { VideoThumbnailService } from '../../services/videoThumbnailService';
import { getOrderedChunks } from '../../utils/downloadChunkedBlob';
import { SceneAutoSaveService } from '../../services/sceneAutoSave';
import useRecorderContext from '../../context/useRecorderContext';

const { Text, Title } = Typography;

interface SceneRecordingControllerProps {
  isRecording: boolean;
  onStartRecording: () => void;
  onStopRecording: () => void;
  currentVideoGuid?: string;
  onSceneComplete?: (scene: Scene) => void;
}

export const SceneRecordingController: React.FC<SceneRecordingControllerProps> = ({
  isRecording,
  onStartRecording,
  onStopRecording,
  currentVideoGuid,
  onSceneComplete,
}) => {
  const [currentScene, setCurrentScene] = useAtom(currentSceneAtom);
  const [sceneList] = useAtom(sceneListAtom);
  const [recordingState, setRecordingState] = useAtom(sceneRecordingStateAtom);
  const [, goToNextScene] = useAtom(nextSceneAtom);
  const [, goToPreviousScene] = useAtom(previousSceneAtom);
  const [activeProject] = useAtom(activeProjectAtom);
  const [, markSceneRecorded] = useAtom(markSceneRecordedAtom);
  const [, updateSceneInList] = useAtom(updateSceneInListAtom);
  const [, showRecordingPreview] = useAtom(showRecordingPreviewAtom);
  const [, updateProjectDuration] = useAtom(updateProjectDurationAtom);
  
  // Get microphone stream state from RecorderContext
  const { microphoneStream } = useRecorderContext();
  
  // Track recording start time for accurate duration calculation
  const [recordingStartTime, setRecordingStartTime] = React.useState<number | null>(null);

  useEffect(() => {
    if (isRecording && currentScene) {
      setRecordingState((prev) => ({
        ...prev,
        isRecording: true,
        currentSceneIndex: currentScene.order,
      }));

      const updatedScene = { ...currentScene, status: 'recording' as const };
      updateSceneInList(updatedScene);
    } else if (!isRecording && recordingState.isRecording && currentScene) {
      setRecordingState((prev) => ({
        ...prev,
        isRecording: false,
      }));
    }
  }, [isRecording, currentScene, recordingState.isRecording, setRecordingState, updateSceneInList]);

  const handleStartRecording = useCallback(() => {
    if (!currentScene) {
      logger.warn('[SceneRecordingController] No current scene selected');
      return;
    }

    // Save recording start time for accurate duration calculation
    const startTime = Date.now();
    setRecordingStartTime(startTime);
    
    console.log('🎬 [SceneRecordingController] Starting recording for scene:', currentScene.title, {
      startTime,
      startTimeISO: new Date(startTime).toISOString()
    });
    onStartRecording();
  }, [currentScene, onStartRecording]);

  const handleStopRecording = useCallback(async () => {
    if (!currentScene || !activeProject) {
      logger.warn('[SceneRecordingController] No current scene or project');
      return;
    }

    // Calculate actual recording duration from timestamps
    const endTime = Date.now();
    const duration = recordingStartTime ? endTime - recordingStartTime : 0;

    console.log('🛑 [SceneRecordingController] Stopping recording for scene:', currentScene.title, {
      startTime: recordingStartTime,
      endTime,
      durationMs: duration,
      durationSeconds: (duration / 1000).toFixed(2)
    });
    onStopRecording();

    if (currentVideoGuid) {
      try {
        // Query ALL chunks for this videoGuid from IndexedDB with retry logic
        // Race condition fix: Encoder worker may have finished but IndexedDB writes may still be pending
        let chunks: any[] = [];
        let chunkIds: string[] = [];
        const maxRetries = 3;
        
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          chunks = await db.chunks.where('videoGuid').equals(currentVideoGuid).toArray();
          chunkIds = chunks.map(chunk => chunk.id).sort();
          
          if (chunkIds.length > 0) {
            logger.info(`[SceneRecordingController] Retrieved ${chunkIds.length} chunks for scene on attempt ${attempt}:`, {
              sceneTitle: currentScene.title,
              videoGuid: currentVideoGuid,
              chunkIds,
              calculatedDuration: duration
            });
            break;
          }
          
          if (attempt < maxRetries) {
            const delay = attempt * 100; // Exponential backoff: 100ms, 200ms, 300ms
            logger.warn(`[SceneRecordingController] No chunks found on attempt ${attempt}, retrying in ${delay}ms...`);
            await new Promise(resolve => setTimeout(resolve, delay));
          } else {
            logger.error(`[SceneRecordingController] Failed to find chunks after ${maxRetries} attempts`);
            throw new Error(`No video chunks found for scene after ${maxRetries} attempts. The recording may have failed.`);
          }
        }
        
        // Save ALL chunks to scene (not just chunk 0!)
        await SceneService.addSceneChunks(currentScene.id, chunkIds);
        
        // CRITICAL: Save the calculated duration to the scene and update project totalDuration
        await SceneService.updateSceneDuration(activeProject.id, currentScene.id, duration);
        logger.info(`[SceneRecordingController] Scene duration saved to DB: ${duration}ms (${(duration / 1000).toFixed(2)}s)`);
        
        // CRITICAL: Update the Jotai state atoms so UI reflects the new duration immediately
        updateProjectDuration({ sceneId: currentScene.id, duration });
        logger.info(`[SceneRecordingController] Scene duration updated in state atoms`);
        
        // Generate thumbnails from video for transition previews
        let videoBlob: Blob | undefined;
        try {
          logger.info('[SceneRecordingController] Generating thumbnails for scene:', currentScene.title);
          const { rows } = await getOrderedChunks(currentVideoGuid);
          videoBlob = new Blob(rows.map(r => r.blob), { type: 'video/webm' });
          
          const { firstFrame, lastFrame } = await VideoThumbnailService.extractBothFrames(videoBlob, {
            width: 320,
            height: 180,
            quality: 0.85,
            format: 'jpeg'
          });
          
          await SceneService.updateScene(currentScene.id, {
            thumbnail: firstFrame.dataUrl,
            lastFrameThumbnail: lastFrame.dataUrl,
          });
          
          const updatedSceneWithThumbnails = {
            ...currentScene,
            thumbnail: firstFrame.dataUrl,
            lastFrameThumbnail: lastFrame.dataUrl,
          };
          updateSceneInList(updatedSceneWithThumbnails);
          
          logger.info('[SceneRecordingController] Thumbnails generated and saved successfully');
        } catch (thumbnailError) {
          logger.warn('[SceneRecordingController] Failed to generate thumbnails, continuing anyway:', thumbnailError);
        }

        // NOTE: Video upload happens LIVE during recording via upload worker
        // No need to upload again here - chunks are already uploaded to S3
        // Thumbnail generation and final scene save happens in RecordingPreviewModal when user clicks "Looks Good"
        logger.info('[SceneRecordingController] Video chunks already uploaded live to S3 during recording, preview modal will handle final save');
        
        // Show preview modal with calculated duration (not corrupted video.duration)
        console.log('🎯 [SceneRecordingController] Opening preview with calculated duration:', {
          duration,
          durationSeconds: (duration / 1000).toFixed(2),
          videoGuid: currentVideoGuid,
          sceneId: currentScene.id
        });
        
        const wasMicEnabled = !!microphoneStream;
        console.log('🎤 [SceneRecordingController] Mic enabled during recording:', {
          microphoneStream: !!microphoneStream,
          wasMicEnabled,
          sceneId: currentScene.id
        });
        
        showRecordingPreview({
          mode: 'scene',
          videoGuid: currentVideoGuid,
          sceneId: currentScene.id,
          scene: currentScene,
          duration, // Pass actual recording duration in milliseconds
          micEnabled: wasMicEnabled, // Check if microphone was enabled during recording
        });

        console.log('✅ [SceneRecordingController] Preview modal opened with duration:', duration);
      } catch (error) {
        logger.error(`[SceneRecordingController] Failed to save scene chunks: ${error}`);
        await SceneService.updateSceneStatus(currentScene.id, 'failed', activeProject?.id);
      }
    }
    
    // Reset start time for next recording
    setRecordingStartTime(null);
  }, [currentScene, activeProject, currentVideoGuid, recordingStartTime, onStopRecording, showRecordingPreview, microphoneStream]);

  const handleNextScene = useCallback(() => {
    if (isRecording) {
      logger.warn('[SceneRecordingController] Cannot change scene while recording');
      return;
    }

    goToNextScene();
    logger.info('[SceneRecordingController] Moved to next scene');
  }, [isRecording, goToNextScene]);

  const handlePreviousScene = useCallback(() => {
    if (isRecording) {
      logger.warn('[SceneRecordingController] Cannot change scene while recording');
      return;
    }

    goToPreviousScene();
    logger.info('[SceneRecordingController] Moved to previous scene');
  }, [isRecording, goToPreviousScene]);

  const captureThumbnail = useCallback(async (canvas: HTMLCanvasElement): Promise<string> => {
    return new Promise((resolve, reject) => {
      try {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to create thumbnail blob'));
              return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
              const base64String = reader.result as string;
              resolve(base64String);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          },
          'image/jpeg',
          0.8
        );
      } catch (error) {
        reject(error);
      }
    });
  }, []);

  const handleCaptureThumbnail = useCallback(async () => {
    if (!currentScene || !activeProject) {
      logger.warn('[SceneRecordingController] No scene to capture thumbnail');
      return;
    }

    try {
      const canvas = document.querySelector('#recording-area canvas') as HTMLCanvasElement;
      if (!canvas) {
        throw new Error('Canvas not found');
      }

      const thumbnail = await captureThumbnail(canvas);
      
      await SceneService.setSceneThumbnail(currentScene.id, thumbnail);
      
      const updatedScene = { ...currentScene, thumbnail };
      updateSceneInList(updatedScene);

      logger.info(`[SceneRecordingController] Thumbnail captured for scene: ${currentScene.title}`);
    } catch (error) {
      logger.error(`[SceneRecordingController] Failed to capture thumbnail: ${error}`);
    }
  }, [currentScene, activeProject, captureThumbnail, updateSceneInList]);

  if (!currentScene || sceneList.length === 0) {
    return null;
  }

  const progress = (recordingState.recordedScenes / recordingState.totalScenes) * 100;
  const canGoNext = recordingState.currentSceneIndex < sceneList.length - 1;
  const canGoPrevious = recordingState.currentSceneIndex > 0;

  return (
    <Card
      size="small"
      style={{
        position: 'fixed',
        top: '120px',
        right: '24px',
        width: '320px',
        zIndex: 1001,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      }}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <Text type="secondary">Scene {recordingState.currentSceneIndex + 1} of {recordingState.totalScenes}</Text>
          <Title level={5} style={{ margin: '8px 0' }}>
            {currentScene.title}
          </Title>
          {currentScene.script && (
            <Text type="secondary" ellipsis>
              {currentScene.script}
            </Text>
          )}
        </div>

        <Progress
          percent={Math.round(progress)}
          strokeColor="#52c41a"
          format={(percent) => `${recordingState.recordedScenes}/${recordingState.totalScenes} recorded`}
        />

        {currentScene.status === 'recorded' && (
          <Alert
            message="Scene Recorded"
            description="This scene has been recorded successfully"
            type="success"
            showIcon
            closable={false}
          />
        )}

        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Button
            icon={<ChevronLeft size={16} />}
            onClick={handlePreviousScene}
            disabled={!canGoPrevious || isRecording}
          >
            Previous
          </Button>

          {!isRecording ? (
            <Button
              type="primary"
              icon={<PlayCircle size={16} />}
              onClick={handleStartRecording}
              disabled={currentScene.status === 'recorded'}
            >
              Record
            </Button>
          ) : (
            <Button
              danger
              icon={<Square size={16} />}
              onClick={handleStopRecording}
            >
              Stop
            </Button>
          )}

          <Button
            icon={<ChevronRight size={16} />}
            onClick={handleNextScene}
            disabled={!canGoNext || isRecording}
          >
            Next
          </Button>
        </Space>

        {currentScene.status === 'recorded' && (
          <Button
            block
            icon={<Camera size={16} />}
            onClick={handleCaptureThumbnail}
            disabled={isRecording}
          >
            Update Thumbnail
          </Button>
        )}

        {currentScene.suggestedDuration && (
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Suggested duration: {Math.floor(currentScene.suggestedDuration / 1000)}s
          </Text>
        )}
      </Space>
    </Card>
  );
};

export default SceneRecordingController;
